import {
  createHtmlElementFromString,
  createPatientGroupIconsFromString,
  createUseIconsFromId,
  formatText,
  getFallbackIconForCategory
} from '../../utils/html.utils';
import { Icon } from '../../general/icons/Icon';
import {
  ageFilterNewId,
  ageFilterUpdatedId,
  getAllProducts,
  HamiltonProductResult,
  HamiltonProductResults,
  packagingUnitOrDefault
} from '../../api/products-api';
import {
  getPriceWithCurrency,
  getPriceWithCurrencyForOnePiece,
  updateSelectedCustomerId
} from '../../api/shopping-cart-api';
import { currentBreakpoint } from '../../utils/resize.utils';
import { FirstLevelMenuItem, SecondLevelMenuItem } from '../../api/menu-api';
import { taxonomies } from '../../taxonomies.module';
import { PageModule } from '../../utils/page.module';
import { header } from '../header/header.module';
import { getUrlForImage } from '../../utils/image.utils';
import { addToShoppingCart } from './e-catalog.utils';
import { eCatalog, ECatalogView } from './e-catalog.module';
import { Param, readUrlParameter, writeToUrl } from '../../utils/url.utils';
import { TaxonomyCategory } from '../../api/taxonomies-api';
import { wishListData } from '../../wish-list-data.module';
import { userData } from '../../user-data.module';
import { shoppingCart } from '../shopping-cart/shopping-cart.module';
import { wishList } from '../wish-list/wish-list.module';
import { scrollToTop } from '../../utils/helper.utils';
import { breakpointListener } from '../../utils/breakpoint-listener.module';
import { isScrollingAllowed } from '../../utils/body-scroll.utils';

/**
 * Initializes and renders the e-catalog products.
 */
export class ECatalogProducts extends PageModule {
  /**
   * Selector of e-catalog view
   */
  componentSelector = '.e-catalog-products-view';

  /**
   * Markup for the products view
   */
  template = `<div class="e-catalog-products-view">
      <aside class="filter__aside">
        <div class="filter-sidebar">
        </div>
    </aside>
    <div class="filter filter--open" id="e-catalog-filter">
      <div class="filter__content">
        <div class="e-catalog-products"></div>
        <div class="indicators indicators--loading">
          <span class="indicator indicator--active"></span>
          <span class="indicator indicator--active"></span>
          <span class="indicator indicator--active"></span>
        </div>
      </div>
    </div>
  </div>`;

  /**
   * Template for the customer dropdown of the filter.
   */
  customerDropdownTemplate = `
  <div class="filter-section">
      <label class="form-control__label-outside" for="filter-dropdown"></label>
      <div class="form-control form-control--dropdown">
          <!--<button class="button button&#45;&#45;dropdown" id="filter-dropdown"></button>-->
          <input type="text" class="button button--dropdown button--dropdown__editable" id="filter-dropdown"/>
          <ul class="dropdown" data-reference-id="filter-dropdown">
          </ul>
      </div>
  </div>`;

  /**
   * Template for a row in the customer dropdown of the filter.
   */
  customerDropdownItemTemplate = '<li class="dropdown__item" tabindex="0"></li>';

  /**
   * Stores the product results.
   */
  productResults: HamiltonProductResults = null;

  /**
   * List of currently selected taxonomies IDs (from the filter).
   */
  currentFilters: string[] = [];

  /**
   * Current search phrase that the user typed into the search field.
   */
  currentSearchPhrase: string = null;

  /**
   * The customer number that the user selected in the customer dropdown inside the filter.
   */
  currentCustomerId: string = null;

  /**
   * The ventilator ID that the user selected in the ventilator dropdown in the header.
   */
  currentVentilatorId: string = null;

  /**
   * Currently selected first level menu item.
   */
  currentFirstLevelMenuItem: FirstLevelMenuItem = null;

  /**
   * Currently selected second level menu item.
   */
  currentSecondLevelMenuItem: SecondLevelMenuItem = null;

  /**
   * Parameter that filters the available products and must be specified with each API call.
   */
  paths = '';

  /**
   * Variable to prevent multiple calls to load more products. Is set to false
   * as long as new products are loaded from the API.
   */
  isLoadingProducts = false;
  /**
   * Variable that indicates if all data that is necessary for this page has been loaded already.
   * E.g. if the taxonomy categories and menu items have been loaded. This is helpful for only
   * rendering the filter when all data is available.
   */
  wasInitialized = false;
  /**
   * Keeps track of how many products are loaded/shown at the moment.
   */
  currentNumberOfProducts = 10;

  /**
   * Number of products to load when the user reaches the end of the product list.
   */
  productNumberStepSize = 10;

  /**
   * When the user reaches the bottom of the page minus this distance in px, new products
   * get loaded automatically.
   */
  loadingDistanceToBottom = 400;

  /**
   * Number of products that should be loaded in the beginning on different breakpoints. Is also the
   * step size which determines how many products will be loaded on scroll when the user reaches the end
   * of the page.
   */
  numberOfProductsToDisplay = {
    large: 20,
    desktop: 9,
    tablet: 6,
    phone: 4
  };

  /**
   * The taxonomy categories.
   */
  taxonomyCategories: TaxonomyCategory[] = [];

  /**
   * One row in the 'additional info' section of an e-catalog tile.
   */
  tileAdditionalInfoRow = `
  <tr>
    <th></th>
    <td></td>
  </tr>`;

  /**
   * Markup for an e-catalog tile.
   */
  eCatalogTileMarkup = `
  <div class="tile tile--e-catalog" data-animate-when-visible="fade-in-up">
    <div class="tile__header" role="button">
        <h5 class="tile__subtitle"></h5>
        <span class="e-catalog__tile-tag"></span>
    </div>
    <div class="tile__media">
        <div class="tile__image" role="button">
            <img src="" alt="Image of E-Catalog Tile" />
        </div>
        <div class="tile__icons">
            <div class="tile__info">
                <div class="packing-unit" title="packing unit"></div>
            </div>
            <div class="discount" role="button" title="Opens discount tooltip"></div>
        </div>
    </div>
    <div class="tile__content">
        <p class="tile__headline"></p>
        <table class="tile__info-table">
        </table>
    </div>
    <div class="tile__footer">
        <button class="button button--small button--icon-only tile__pin-button" data-overlay-id="select-wish-list-overlay" aria-label="Wishlist">
            <span class="button__icon-single">
                ${Icon.pin}
            </span>
        </button>
        <!--<button class="button button--small button--icon-only button-quick-info">
            <span class="button__icon-single">${Icon.info}</span>
        </button>-->
        <div class="button-section button-section--no-margin button-section--row-spacing button-section--right">
            <button class="button button--small tile__add-to-cart-button">
                <span class="button__text"></span>
                <span class="button__icon-right">
                    ${Icon.shopping}
                </span></button>
            <a class="button button--small tile__details-button">
                <span class="button__text"></span>
                <span class="button__icon-right">
                    ${Icon.rightArrow}
                </span>
            </a>
        </div>
    </div>
</div>
  `;
  /**
   * Markup for the discount tooltip shown on e-catalog tiles.
   */
  discountTooltipMarkup = `
<div class="discount-tooltip" aria-hidden="true">
    <div class="discount-tooltip__title"></div>
    <div class="discount-tooltip__subtitle"></div>
    <table>
        <tr>
            <th></th>
            <th></th>
        </tr>
    </table>
</div>`;

  /**
   * Markup for a section in the filter.
   * Contains an accordion that toggles the filter options (taxonomies)
   * that belong to this filter section (taxonomy category).
   */
  filterSectionMarkup = `
<div class="filter-section">
    <div class="accordion accordion--filter">
        <button class="accordion__header"></button>
        <div class="accordion__content">
            <div class="interactive-tags interactive-tags--multiselect">
            </div>
        </div>
    </div>
</div>`;

  /**
   * Empty state for the product tiles when no tiles are available.
   */
  productsEmptyState = `
<div class="e-catalog-products__empty-state">
    <h4 class="e-catalog-products__empty-state-title"><span class="text-color-light"></span></h4>
    <span class="e-catalog-products__empty-state-message"></span>
    <div class="button-section button-section--small-margin">
      <button class="button">
        <span class="button__text"></span>
      </button>
    </div>
</div>`;

  /**
   * Markup for the input element of a filter option.
   */
  filterTagInputMarkup = '<input type="checkbox">';

  /**
   * Markup for the label element of a filter option.
   */
  filterTagLabelMarkup = '<label><span></span><span class="tag tag--counter"></span></label>';

  /**
   * Setup the component with translations
   * @param translations The translations to initialize
   * @param paths Paths parameter for product-list requests.
   */
  constructor(translations: { [key: string]: string }, paths: string) {
    super();
    this.translations = translations;
    this.paths = paths;
  }

  /**
   * Update the number of products that should be loaded depending on the current breakpoint
   */
  resetNumberOfProductsBeingLoaded(): void {
    const breakpoint = currentBreakpoint();
    if (breakpoint.isLarge) {
      this.currentNumberOfProducts = this.numberOfProductsToDisplay.large;
      this.productNumberStepSize = this.numberOfProductsToDisplay.large;
    } else if (breakpoint.isDesktop) {
      this.currentNumberOfProducts = this.numberOfProductsToDisplay.desktop;
      this.productNumberStepSize = this.numberOfProductsToDisplay.desktop;
    } else if (breakpoint.isTablet) {
      this.currentNumberOfProducts = this.numberOfProductsToDisplay.tablet;
      this.productNumberStepSize = this.numberOfProductsToDisplay.tablet;
    } else {
      this.currentNumberOfProducts = this.numberOfProductsToDisplay.phone;
      this.productNumberStepSize = this.numberOfProductsToDisplay.phone;
    }
  }

  /**
   * Create the e-catalog. Fetches the product information and renders the product tiles.
   */
  renderView(): void {
    // show the sub nav
    header.showSubNav(true);

    // set the page title
    document.title = this.translations.headerTitle;

    // don't hide the headers shadow before user scrolled
    header.setHideShadowBeforeScrolled(false);

    // Add to top button with translation
    const content = this.pageComponent.querySelector<HTMLElement>('.filter__content');
    content.dataset.toTop = this.translations.toTop;

    // set loading state until the filters are loaded
    this.renderFilterLoadingState();
    this.setupFilterTranslations();

    // now fetch the products
    this.resetNumberOfProductsBeingLoaded();
    this.showLoadingIndicator(true);

    // first load the customerIds because we need to know which customers exist for the current user
    userData.getCachedCustomerIdsForCurrentUser().then((customerIds) => {
      // First of all retrieve the taxonomies and their categories
      taxonomies.getCachedTaxonomyCategories().then(async (taxonomyCategories) => {
        this.taxonomyCategories = taxonomyCategories;
        this.loadFiltersFromUrl();

        // Load the selected customer from URL or API
        await this.loadSelectedCustomer(customerIds);

        // now fetch the products
        this.fetchProductData(this.productNumberStepSize).then((products) => {
          if (!this.wasInitialized) {
            this.setupFilter();
          }

          this.wasInitialized = true;
          this.productResults = products;
          this.renderProductTiles();

          // create listener for infinite scrolling
          this.setupInfiniteScrollListener();
          this.renderFilter();
        });
      });
    });
  }

  /**
   * Updates the height and position of the filter and filter content area to fill the remaining
   * space below the main navigation and tab bars.
   */
  updateFilterPosition(): void {
    // update the filter position
    const breakpoint = currentBreakpoint();
    const isMobile = breakpoint.isMobilePortrait || breakpoint.isMobileLandscape;
    if (!isMobile) {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const mainNavigationSticky =
        document.querySelector<HTMLElement>('.main-navigation-wrapper').style.position === 'sticky';
      scrollTop === 0
        ? header.updateFilterPositionDocumentTop()
        : mainNavigationSticky
        ? header.updateFilterPositionScrolledUp()
        : header.updateFilterPositionScrolledDown();
    }
  }

  /**
   * Reset all filters. Removes all filters that can be selected in the filter sidebar. Updates the URL and re-renders the product results.
   */
  resetFilters(): void {
    this.currentFilters = [];
    this.updateFiltersInUrl();
    this.renderView();
  }

  /**
   * Fill the filter element with the translated labels.
   */
  setupFilterTranslations(): void {
    const filterElement = this.pageComponent.querySelector<HTMLElement>('.filter');
    filterElement.dataset.labelButton = this.translations.filterShowAll;
    filterElement.dataset.labelFilter = this.translations.filter;
    filterElement.dataset.labelReset = this.translations.mobileResetFilter;
  }

  /**
   * If the current URL includes any filters, load them so only the filtered products are shown to the user.
   */
  loadFiltersFromUrl(): void {
    const filtersFromUrl = readUrlParameter(Param.FILTER) || '';
    // collect the filters that need to be displayed
    const filterCategories = this.getFilterCategoriesForCurrentMenuSelection();
    this.currentFilters = [];

    // manually add the age filter IDs because they are not
    // part of the taxonomies
    if (filtersFromUrl.includes(ageFilterNewId)) {
      this.currentFilters.push(ageFilterNewId);
    }
    if (filtersFromUrl.includes(ageFilterUpdatedId)) {
      this.currentFilters.push(ageFilterUpdatedId);
    }

    // add all filters that are valid in the current selected category
    for (const taxonomyCategory of filterCategories) {
      for (const taxonomy of taxonomyCategory.taxonomies) {
        const taxonomyId = taxonomy['@id'];
        if (filtersFromUrl.includes(taxonomyId)) {
          this.currentFilters.push(taxonomyId);
        }
      }
    }

    // load the ventilator from the URL
    const ventilatorId = readUrlParameter(Param.VENTILATOR);
    if (ventilatorId) {
      this.currentVentilatorId = ventilatorId;
    }

    // finally clear the URL filters that are not applicable anymore
    this.updateFiltersInUrl(true, true);
  }

  /**
   * Load the customerID that should be selected. Prefers to load the customerID from the URL.
   * If no ID in URL given, loads the last selected ID from the shopping cart API. If that is also
   * not given, preselects the first available customer ID as a default.
   */
  async loadSelectedCustomer(customerIds: string[]): Promise<void> {
    // read customer ID
    const customerIdFromUrl = readUrlParameter(Param.CUSTOMER);
    if (customerIdFromUrl) {
      // first try to load a customer ID from the URL
      this.currentCustomerId = customerIdFromUrl;
      userData.lastSelectedCustomerId = customerIdFromUrl;
    } else {
      // If no customer ID in URL, try to load last selected customer from API
      const lastCustomerIdFromApi = await userData.getCachedLastSelectedCustomerId();
      if (lastCustomerIdFromApi) {
        // Use the one from the api if available
        this.currentCustomerId = lastCustomerIdFromApi;
      } else if (customerIds && customerIds.length > 0) {
        // otherwise fallback -> select the first customer ID if no ID selected before
        const firstCustomerId = customerIds[0];
        this.currentCustomerId = firstCustomerId;
        userData.lastSelectedCustomerId = firstCustomerId;
        writeToUrl({ param: Param.CUSTOMER, value: firstCustomerId }, null, true);
      }
    }
  }

  /**
   * Get the filter category IDs for all filters that should be displayed for the current combination of selected
   * category and selected tab (first & second level menu item).
   */
  getFilterCategoriesForCurrentMenuSelection(): TaxonomyCategory[] {
    const filterCategories = [];
    // If there is a tab selected (second level), then show filters for this tab
    if (this.currentSecondLevelMenuItem && this.currentSecondLevelMenuItem.filters) {
      for (const categoryId of this.currentSecondLevelMenuItem.filters) {
        const filterCategory = this.taxonomyCategories.find((category) => category['@id'] === categoryId);
        if (filterCategory) {
          filterCategories.push(filterCategory);
        } else {
          /* eslint-disable no-console */
          console.error(
            `Could not find taxonomy for filter with ID ${categoryId}. Is this filter missing in the /taxonomies API?`
          );
        }
      }
    } else if (this.currentFirstLevelMenuItem) {
      // if no tab but a category is selected, then the 'all' option in the second level is selected.
      // Means we display all filters for all second level menu items
      const filterIds = new Set();
      for (const tab of this.currentFirstLevelMenuItem.secondaryCategories) {
        if (tab.filters) {
          for (const categoryId of tab.filters) {
            filterIds.add(categoryId);
          }
        }
      }

      for (const categoryId of filterIds) {
        const filterCategory = this.taxonomyCategories.find((category) => category['@id'] === categoryId);
        if (filterCategory) {
          filterCategories.push(filterCategory);
        } else {
          /* eslint-disable no-console */
          console.error(
            `Could not find taxonomy for filter with ID ${categoryId}. Is this filter missing in the /taxonomies API?`
          );
        }
      }
    } else {
      // if no category and no tab is selected, we display filters for 'all' categories and 'all' tabs
      const filterIds = new Set();
      for (const category of eCatalog.menuItems) {
        for (const tab of category.secondaryCategories) {
          if (tab.filters) {
            for (const categoryId of tab.filters) {
              filterIds.add(categoryId);
            }
          }
        }
      }

      for (const categoryId of filterIds) {
        const filterCategory = this.taxonomyCategories.find((category) => category['@id'] === categoryId);
        if (filterCategory) {
          filterCategories.push(filterCategory);
        } else {
          /* eslint-disable no-console */
          console.error(
            `Could not find taxonomy for filter with ID ${categoryId}. Is this filter missing in the /taxonomies API?`
          );
        }
      }
    }
    return filterCategories;
  }

  /**
   * Create skeletons that display a loading state for the filter.
   */
  renderFilterLoadingState(): void {
    const filterContent = document.querySelector<HTMLElement>('.filter-sidebar');
    if (filterContent) {
      for (const child of filterContent.querySelectorAll('.filter-section')) {
        child.remove();
      }

      for (let i = 0; i < 3; i++) {
        const filterSection = createHtmlElementFromString(this.filterSectionMarkup);
        const accordion = filterSection.querySelector('.accordion');
        accordion.classList.add('accordion--skeleton');
        const tagContainer = filterSection.querySelector<HTMLElement>('.interactive-tags');
        tagContainer.classList.add('interactive-tags--skeleton');

        for (let i = 0; i < 4; i++) {
          this.createFilterTag('', null, false, false, '', '', tagContainer, true);
        }

        // finally append the filter section to the filter sidebar
        filterContent.appendChild(filterSection);
      }
    }
  }

  /**
   * Called when the user selected a ventilator from the ventilator dropdown.
   * @param ventilatorId: Id of the chosen ventilator.
   * @param reload: Boolean if the page should reload the products after the item changed.
   */
  async updateFilterWithNewVentilator(ventilatorId: string, reload = true): Promise<void> {
    if (this.currentVentilatorId !== ventilatorId) {
      this.currentVentilatorId = ventilatorId;

      if (reload && this.wasInitialized) {
        await this.updateFilterSettings();
        this.renderFilter();
      }
    }
  }

  /**
   * Called when the user typed in something in the search bar.
   * @param searchPhrase: Free text search from the user.
   * @param reload: Boolean if the page should reload the products after the item changed.
   */
  async updateFilterWithNewSearchPhrase(searchPhrase: string, reload = true): Promise<void> {
    if (this.currentSearchPhrase !== searchPhrase) {
      this.currentSearchPhrase = searchPhrase;

      if (reload && this.wasInitialized) {
        await this.updateFilterSettings();
        this.renderFilter();
      }
    }
  }

  /**
   * When the user selects a menu item from the first level, this function is called to update the products
   * to only those, that belong to the selected menu item.
   * @param firstLevelMenuItem: The selected menu item.
   * @param reload: Boolean if the page should reload the products after the item changed.
   */
  async updateFirstLevelMenuSelection(firstLevelMenuItem: FirstLevelMenuItem, reload = true): Promise<void> {
    this.currentFirstLevelMenuItem = firstLevelMenuItem;
    this.currentSecondLevelMenuItem = null;

    if (reload && this.wasInitialized) {
      await this.updateFilterSettings();
      this.renderFilter();
    }

    if (firstLevelMenuItem === null) {
      this.updateSecondLevelMenuSelection(null, reload);
    }
  }

  /**
   * When the user selects a menu item from the second level, this function is called to update the products
   * to only those, that belong to the selected menu item.
   *
   * @param secondLevelMenuItem: The selected menu item or null if the 'all' option was selected.
   * @param reload: Boolean if the page should reload the products after the item changed.
   */
  async updateSecondLevelMenuSelection(secondLevelMenuItem: SecondLevelMenuItem, reload = true): Promise<void> {
    this.currentSecondLevelMenuItem = secondLevelMenuItem;
    if (reload && this.wasInitialized) {
      await this.updateFilterSettings();
      this.renderFilter();
    }
  }

  /**
   * Load the products and filters depending on the current filter settings.
   */
  async updateFilterSettings(): Promise<void> {
    // first remove filters, that are not applicable to the current menu selection
    this.removeInapplicableFilters();

    // now fetch the products
    this.showLoadingIndicator(true);
    this.resetNumberOfProductsBeingLoaded();
    this.productResults = await this.fetchProductData(this.productNumberStepSize);
    this.renderProductTiles();
    return;
  }

  /**
   * When the user switches between menus (categories) or sub menus (tabs), the
   * filters change. This function removes any filter from the URL and currentFilters
   * that are not applicable to the current menu selection anymore.
   */
  removeInapplicableFilters(): void {
    const filtersFromUrl = readUrlParameter(Param.FILTER) || '';
    // collect the filters for the current category/tab
    const filterCategories = this.getFilterCategoriesForCurrentMenuSelection();
    this.currentFilters = [];

    // we need to remove all filters that are not applicable to the current
    // menu selection from the url. This list contains only the valid filters
    // that will be added to the url after clearing it.
    const newFiltersForUrl = [];

    // manually add the age filter IDs because they are not
    // part of the taxonomies
    if (filtersFromUrl.includes(ageFilterNewId)) {
      this.currentFilters.push(ageFilterNewId);
      newFiltersForUrl.push(ageFilterNewId);
    }
    if (filtersFromUrl.includes(ageFilterUpdatedId)) {
      this.currentFilters.push(ageFilterUpdatedId);
      newFiltersForUrl.push(ageFilterUpdatedId);
    }

    // add all filters that are valid in the current selected category
    for (const taxonomyCategory of filterCategories) {
      for (const taxonomy of taxonomyCategory.taxonomies) {
        const taxonomyId = taxonomy['@id'];
        if (filtersFromUrl.includes(taxonomyId)) {
          this.currentFilters.push(taxonomyId);
          newFiltersForUrl.push(taxonomyId);
        }
      }
    }

    // finally, clear the URL filters that are not applicable anymore
    this.updateFiltersInUrl(true, true);
  }

  /**
   * Setup listeners for the filter.
   */
  setupFilter(): void {
    // initialize listener for the reset button on mobile
    const resetButton = document.querySelector('.filter-header__reset-button');
    resetButton?.addEventListener('click', () => {
      this.resetFilters();
    });

    // listen for breakpoint changes between mobile and desktop
    breakpointListener.listenForBreakpointChangeBetweenMobileAndDesktop(() => {
      this.updateFilterPosition();
    });
  }

  /**
   * Render the filter sidebar for the given second level menu item.
   */
  renderFilter(): void {
    // Scroll to top, because products are reloaded
    scrollToTop();

    // set loading state until the filters are loaded
    this.renderFilterLoadingState();

    // To find out which filters we should display for the selected second level menu item,
    // we need to look up the taxonomies from the IDs that we get from the second level menu item.
    // First fetch all taxonomies/taxonomy categories. Then look up the necessary filters.

    // remove all existing filters
    const filterContent = document.querySelector<HTMLElement>('.filter-sidebar');
    if (filterContent) {
      for (const child of filterContent.querySelectorAll('.filter-section')) {
        child.remove();
      }

      userData.getCachedCustomerIdsForCurrentUser().then((customerIds: string[]) => {
        this.createCustomerDropdown(customerIds, filterContent);
      });

      // collect the filters that need to be displayed
      const filterCategories = this.getFilterCategoriesForCurrentMenuSelection();

      for (const taxonomyCategory of filterCategories) {
        const filterSection = createHtmlElementFromString<HTMLElement>(this.filterSectionMarkup);
        const tagContainer = filterSection.querySelector<HTMLElement>('.interactive-tags');
        const categoryLabel = taxonomyCategory.label || taxonomyCategory.name || taxonomyCategory['@name'];
        filterSection.querySelector<HTMLElement>('.accordion__header').innerText = categoryLabel;
        const categoryId = taxonomyCategory['@id'];

        // 'All' tag
        const allTagId = `${taxonomyCategory['@id']}-all`;

        // Total number of products that belong to this category
        const taxonomyCategoryCount = taxonomyCategory.taxonomies.reduce(
          (totalCount, taxonomy) => totalCount + (this.productResults.taxonomyCounts[taxonomy['@id']] || 0),
          0
        );

        // Check if the filter category has a selected filter, otherwise preselect the 'all' option
        const hasSelectedOption = taxonomyCategory.taxonomies.reduce(
          (hasActiveFilter, taxonomy) => hasActiveFilter || this.currentFilters.includes(taxonomy['@id']),
          false
        );

        if (!hasSelectedOption) { 
          filterSection.querySelector('.accordion--filter').classList.add('accordion--closed');
        }

        // only if the filter category contains any results, we render it
        if (taxonomyCategoryCount > 0) {
          // create the filter tag for 'all results'
          this.createFilterTag(
            this.translations.all,
            null,
            !hasSelectedOption,
            true,
            allTagId,
            categoryId,
            tagContainer
          );

          // and for each filter in this category create a filter tag
          for (const taxonomy of taxonomyCategory.taxonomies) {
            const taxonomyId = taxonomy['@id'];
            const taxonomyCount = this.productResults.taxonomyCounts[taxonomyId];
            const checked = this.currentFilters.includes(taxonomyId);
            const filterName = taxonomy.label || taxonomy.name || taxonomy['@name'];

            this.createFilterTag(filterName, taxonomyCount || 0, checked, false, taxonomyId, categoryId, tagContainer);
          }

          // finally, append the filter section to the filter sidebar
          filterContent.appendChild(filterSection);
        }
      }

      // The new/updated filters are not part of the API, so we need to add them manually
      this.createNewOrUpdatedFilterSection(filterContent);

      // update the filter position initially on page load (for mobile)
      this.updateFilterPosition();

      // update the mobile filter button state
      const mobileFilterButton = document.querySelector('.tag--filter');
      const mobileFilterResetButton = document.querySelector('.tag--filter-secondary');
      if (mobileFilterButton) {
        mobileFilterButton.classList.toggle('tag--filter-inactive', this.currentFilters?.length === 0);
        mobileFilterResetButton.classList.toggle('tag--hidden', this.currentFilters?.length === 0);
      }
    }
    writeToUrl([], [Param.PN]);
  }

  /**
   * Create filter tags for the 'age' filter. Has options 'new' and 'updated'.
   * The new/updated filters are not part of the API, so we need to add them manually
   */
  createNewOrUpdatedFilterSection(filterContent: HTMLElement): void {
    const filterSection = createHtmlElementFromString(this.filterSectionMarkup);
    const tagContainer = filterSection.querySelector<HTMLElement>('.interactive-tags');
    filterSection.querySelector<HTMLElement>('.accordion__header').innerText = this.translations.ageFilterTitle;
    const categoryId = 'filter-age';

    // 'All' tag
    const allTagId = 'age-all';

    // Check if the filter category has a selected filter, otherwise preselect the 'all' option
    const hasSelectedOption =
      this.currentFilters.includes(ageFilterUpdatedId) || this.currentFilters.includes(ageFilterNewId);

    // create the filter tag for 'all results'
    this.createFilterTag(this.translations.all, null, !hasSelectedOption, true, allTagId, categoryId, tagContainer);

    // and for each filter in this category create a filter tag
    this.createFilterTag(
      this.translations.ageFilterNewLabel,
      null,
      this.currentFilters.includes(ageFilterNewId),
      false,
      ageFilterNewId,
      categoryId,
      tagContainer
    );
    this.createFilterTag(
      this.translations.ageFilterUpdatedLabel,
      null,
      this.currentFilters.includes(ageFilterUpdatedId),
      false,
      ageFilterUpdatedId,
      categoryId,
      tagContainer
    );

    // finally append the filter section to the filter sidebar
    filterContent.appendChild(filterSection);
  }

  /**
   * Create the customer dropdown for the filter.
   */
  createCustomerDropdown(customerIds: string[], filterContent: HTMLElement): void {
    if (customerIds && customerIds.length > 0) {
      // render the customer dropdown and label
      const customerDropdown = createHtmlElementFromString(this.customerDropdownTemplate);
      customerDropdown.querySelector<HTMLElement>('.form-control__label-outside').innerText =
        this.translations.customer;

      // For each customer create an option in the dropdown
      const dropdownContainer = customerDropdown.querySelector('ul');
      for (const customerId of customerIds) {
        const customerDropdownOption = createHtmlElementFromString(this.customerDropdownItemTemplate);
        customerDropdownOption.innerText = customerId;
        dropdownContainer.appendChild(customerDropdownOption);
      }

      if (this.currentCustomerId) {
        // if there was a customer set in the URL, select that customer in the dropdown as well
        customerDropdown.querySelector<HTMLElement>('.button--dropdown').value =
          this.currentCustomerId || customerIds[0];
      }

      // insert the customer dropdown at the top of the filter
      const firstFilterSection = filterContent.querySelector('.filter-section');
      if (firstFilterSection) {
        filterContent.insertBefore(customerDropdown, firstFilterSection);
      } else {
        filterContent.appendChild(customerDropdown);
      }

      // Listen for changes in the customer dropdown
      dropdownContainer.addEventListener('onitemselect', async (event: CustomEvent) => {
        const selectedCustomerNumber = event.detail.target.innerText;
        this.currentCustomerId = selectedCustomerNumber;
        userData.lastSelectedCustomerId = selectedCustomerNumber;
        writeToUrl({ param: Param.CUSTOMER, value: selectedCustomerNumber });
        await this.updateFilterSettings();
        this.renderFilter();
        void updateSelectedCustomerId(selectedCustomerNumber);

        // close the shopping cart and wish list
        wishList.closeWishlist();
        shoppingCart.closeShoppingCart();
      });
    }
  }

  /**
   * Create a filter tag with the given properties and append it to the given tagContainer element.
   * @param label: Label of the filter option.
   * @param count: Number of results with this filter option. Displayed in a tag right next to the label.
   * @param checked: If this filter option should be checked or not.
   * @param allOption: If this filter option is an 'all' option that deselects all other options when selected.
   * @param id: ID for the input element.
   * @param categoryId: ID of the filter category (taxonomy category) that is used for the 'name' property of the input group.
   * @param tagContainer: HTML element that holds all tags that belong to the same filter group.
   * @param loadingState: If the tag should only represent a loading state.
   */
  createFilterTag(
    label: string,
    count: number,
    checked: boolean,
    allOption: boolean,
    id: string,
    categoryId: string,
    tagContainer: HTMLElement,
    loadingState = false
  ): void {
    const filterSuffix = '-filter';
    const filterId = id + filterSuffix;

    // If the filter option is currently selected
    const filterIsActive = this.currentFilters.includes(id);
    const filterIsDisabled = count === 0;

    // create the tag input element
    const filterTagInput = createHtmlElementFromString(this.filterTagInputMarkup) as HTMLInputElement;
    filterTagInput.toggleAttribute('checked', checked || filterIsActive);
    filterTagInput.name = categoryId;
    filterTagInput.value = filterId;
    filterTagInput.id = filterId;
    filterTagInput.disabled = filterIsDisabled;

    // mark the 'all' option
    if (allOption) filterTagInput.dataset.checkAll = 'true';

    // append the input to the tag container
    tagContainer.appendChild(filterTagInput);

    if (!loadingState) {
      // listen for changes of the checked state
      filterTagInput.addEventListener('change', (event) => {
        const currentTarget = event.currentTarget as HTMLElement;
        const taxonomyId = currentTarget.id.replace(filterSuffix, '');
        if (filterTagInput.checked) {
          // filter was selected
          if (currentTarget.dataset.checkAll === 'true') {
            // if 'all' option selected -> remove all filters from this taxonomy category
            const options = currentTarget.parentElement.querySelectorAll<HTMLInputElement>('input:not(:first-child)');
            for (const option of options) {
              this.currentFilters = this.currentFilters.filter(
                (taxonomyId) => taxonomyId !== option.id.replace(filterSuffix, '')
              );
            }
            this.updateFiltersInUrl();
          } else {
            // add the filter to the current set of filters
            this.currentFilters.push(taxonomyId);

            if (taxonomyId === ageFilterNewId && this.currentFilters.includes(ageFilterUpdatedId)) {
              // if the 'new' option of the age filter was selected and the 'updated' option is also selected,
              // remove the updated option because they are mutually exclusive
              this.currentFilters = this.currentFilters.filter((item) => item !== ageFilterUpdatedId);
            } else if (taxonomyId === ageFilterUpdatedId && this.currentFilters.includes(ageFilterNewId)) {
              // and the other way round
              this.currentFilters = this.currentFilters.filter((item) => item !== ageFilterNewId);
            }

            this.updateFiltersInUrl();
          }
        } else {
          // filter was removed, remove it from the current filters
          this.currentFilters = this.currentFilters.filter((item) => item !== taxonomyId);
          this.updateFiltersInUrl();
        }

        // finally reload the e-catalog
        this.renderView();
      });
    }

    // create the tag label
    const filterTagLabel = createHtmlElementFromString(this.filterTagLabelMarkup) as HTMLLabelElement;
    filterTagLabel.setAttribute('for', filterId);
    const labelSpan = filterTagLabel.querySelector('span');
    labelSpan.innerText = label;

    // create the tag counter
    const tagCounter = filterTagLabel.querySelector<HTMLElement>('.tag');
    tagCounter.innerText = count + '';

    if (count === null) {
      // remove the count tag if the count passed is null or zero
      // this is used for the 'all' tag for example
      tagCounter.parentElement.removeChild(tagCounter);
    }

    // append the label to the tag container
    tagContainer.appendChild(filterTagLabel);
  }

  /**
   * Update the current URL with the current filter settings.
   */
  updateFiltersInUrl(noHistory = false, noEvent = false): void {
    // write filters from the filter sidebar
    if (this.currentFilters.length > 0) {
      writeToUrl({ param: Param.FILTER, value: this.currentFilters.join(' ') }, null, noHistory, noEvent);
    } else {
      writeToUrl([], [Param.FILTER], noHistory, noEvent);
    }
  }

  /**
   * Update the already visible pin buttons (because wish list items have changed).
   */
  updatePinButtonStates(): void {
    if (this.pageComponent) {
      wishListData
        .getCachedWishListProductIds(false)
        .then((productsInWishlists) => {
          const productTiles = this.pageComponent.querySelectorAll<HTMLElement>('.tile--e-catalog');

          for (const tile of productTiles) {
            const productId = tile.dataset.productId;
            const pinButton = tile.querySelector<HTMLElement>('.tile__pin-button');
            pinButton.classList.toggle('button--primary', productsInWishlists.includes(productId));
          }
        })
        .catch(() => {
          // user logged out -> do nothing
          //pinButton?.parentElement.removeChild(pinButton);
        });
    }
  }

  /**
   * Render the product tiles. Create a tile for each product found for the current filter.
   * @param fromIndex: If provided this function only renders new products from the given index instead of replacing all existing rendered products.
   */
  renderProductTiles(fromIndex: number = null): void {
    // first remove all products
    const productGrid = this.pageComponent.querySelector<HTMLElement>('.e-catalog-products');
    if (!fromIndex) {
      productGrid.innerHTML = '';
    }

    // retrieve the wish list so we can display which products are already in it (blue pin button)
    wishListData
      .getCachedWishListProductIds(false)
      .then((productsInWishlists: string[]) => {
        this.createProductTiles(productGrid, fromIndex, productsInWishlists);
        formatText(productGrid);
      })
      .catch(() => {
        // no user data available / user logged out
        this.createProductTiles(productGrid, fromIndex, null);
        formatText(productGrid);
      });
  }

  /**
   * Create the product tiles. Create a tile for each product found for the current filter.
   * @param productGrid: The grid that contains all product tiles.
   * @param fromIndex: If provided this function only renders new products from the given index instead of replacing all existing rendered products.
   * @param productsInWishlists: The list of products that are currently part of any wish list.
   */
  createProductTiles(productGrid: HTMLElement, fromIndex: number, productsInWishlists: string[]): void {
    // these are the products to display
    const products = this.productResults.results;

    if (this.productResults.totalResults <= this.productNumberStepSize) {
      // in case there are no more products to load, immediately hide the loading indicator
      this.showLoadingIndicator(false);
    }

    const startIndex = fromIndex || 0;
    for (let i = startIndex; i < products.length; i++) {
      const product = products[i];

      // for each product create an e-catalog tile
      const tile = createHtmlElementFromString(this.eCatalogTileMarkup);
      const pinButton = tile.querySelector<HTMLElement>('.tile__pin-button');
      const addToCartButton = tile.querySelector<HTMLElement>('.tile__add-to-cart-button');
      const discountButton = tile.querySelector<HTMLElement>('.discount');
      const statusTag = tile.querySelector<HTMLElement>('.e-catalog__tile-tag');

      tile.dataset.productId = product.nodeid;
      tile.querySelector<HTMLElement>('.tile__subtitle').innerText = product.partNumber ? product.partNumber : '-';
      tile.querySelector<HTMLElement>('.tile__headline').innerText = product.productName;
      tile.querySelector<HTMLElement>('.packing-unit').innerText = packagingUnitOrDefault(product);

      const tag =
        product.newOrUpdated === 'new'
          ? `<span class="tag tag--small tag--green">${this.translations.ageFilterNewLabel}</span>`
          : product.newOrUpdated === 'updated'
          ? `<span class="tag tag--small">${this.translations.ageFilterUpdatedLabel}</span>`
          : '';

      const tileImageContainer = tile.querySelector<HTMLElement>('.tile__image');
      const tileImage = tileImageContainer.querySelector<HTMLImageElement>('img');
      const imageUrl = getUrlForImage(product.imageSmall);

      statusTag.innerHTML = tag;
      if (imageUrl) {
        // if there is an image for the product, show it
        tileImage.src = imageUrl;
      } else {
        // otherwise, show the large version of the category icon for this product
        tileImageContainer.removeChild(tileImage);
        const categoryIconLarge = getFallbackIconForCategory(product.icons, true);
        tile.setAttribute('data-fallback-icons', product.icons ? JSON.stringify(product.icons) : '');
        tileImageContainer.appendChild(categoryIconLarge);
      }

      // Set the correct e-catalog product icon
      const productIcon = getFallbackIconForCategory(product.icons);
      if (productIcon) {
        const tileHeader = tile.querySelector<HTMLElement>('.tile__header');
        tileHeader.insertBefore(productIcon, tileHeader.firstElementChild);
      }

      // Add click listener for detail page
      const detailsButton = tile.querySelector<HTMLAnchorElement>('.tile__details-button');
      detailsButton.querySelector<HTMLElement>('.button__text').innerText = this.translations.productDetailsButton;
      tile.addEventListener('click', () => writeToUrl({ param: Param.PRODUCT, value: product.nodeid }), false);

      pinButton.dataset.overlayAssociatedId = product.nodeid;

      // table with the additional product info, that we need to fill with details
      const additionalInfoTable = tile.querySelector<HTMLTableElement>('.tile__info-table');
      let cellIndex = 0;

      if (product.length) {
        const infoRow = additionalInfoTable.insertRow(cellIndex);
        infoRow.innerHTML = this.tileAdditionalInfoRow;
        infoRow.querySelector('th').innerText = this.translations.productLength;
        infoRow.querySelector('td').innerText = `${product.length} cm`;
        cellIndex++;
      }

      if (product.compatibility) {
        const infoRow = additionalInfoTable.insertRow(cellIndex);
        infoRow.innerHTML = this.tileAdditionalInfoRow;
        infoRow.querySelector('th').innerText = this.translations.productCompatibility;
        infoRow.querySelector('td').innerText = product.compatibility;
        cellIndex++;
      }

      if (product.price != undefined && product.price.length > 0) {
        this.createDiscountsTooltip(product, discountButton);
        const infoRow = additionalInfoTable.insertRow(cellIndex);
        infoRow.innerHTML = this.tileAdditionalInfoRow;
        infoRow.querySelector('th').innerText = this.translations.price;
        infoRow.querySelector('td').innerText = getPriceWithCurrencyForOnePiece(product.price);
        cellIndex++;
      } else {
        // hide the discounts button if there is only a single price
        discountButton.parentElement.removeChild(discountButton);
      }
      // Hide add to cart if product is not software option (taxonomy ID: 8b403b52-321c-4a5a-abbc-0dd4fab6877e)
      if (product.price != undefined && product.price.length > 0 && product.isWebshopUser) {
        
        // add to shopping cart button
        if (product.catalog_taxonomies.indexOf('8b403b52-321c-4a5a-abbc-0dd4fab6877e') > -1) {
          addToCartButton.querySelector<HTMLElement>('.button__text').innerText =
            this.translations.addProductToCartButton;
          addToCartButton.addEventListener(
            'click',
            (event) => {
              event.stopImmediatePropagation();
              addToShoppingCart(
                product,
                this.translations.addedToShoppingCart,
                this.translations.goToShoppingCart,
                this.translations.errorMessage
              );
            },
            false
          );
        } else {
          addToCartButton.parentElement.removeChild(addToCartButton);
        }
      } else {
        // hide discount and add to cart button
        addToCartButton.parentElement.removeChild(addToCartButton);
      }

      const infoContainer = tile.querySelector<HTMLElement>('.tile__info');

      if (product.typeOfUse) {
        const usageInfoElement = createUseIconsFromId(product.typeOfUse, this.translations);
        infoContainer.insertBefore(usageInfoElement, infoContainer.firstChild);
      }

      if (product.patientGroups) {
        const patientGroupInfoElements = createPatientGroupIconsFromString(product.patientGroups, this.translations);
        infoContainer.insertBefore(patientGroupInfoElements, infoContainer.firstChild);
      }

      if (productsInWishlists !== null && !document.querySelector('html').classList.contains('user-logged-out')) {
        // make the pin button primary (blue) if the product is already part of a wish list
        pinButton.classList.toggle('button--primary', productsInWishlists.includes(product.nodeid));
      } else if (document.querySelector('html').classList.contains('user-logged-out')) {
        // remove the pin button
        pinButton.nextElementSibling.style.marginLeft = '0';
        pinButton.parentElement.removeChild(pinButton);
      }

      // finally append the product tile to the grid
      productGrid.appendChild(tile);
    }

    if (!products || products.length === 0) {
      // render empty state
      this.renderProductsEmptyState(productGrid, !!this.currentSearchPhrase);
    } else {
      this.removeEmptyState(productGrid);

      // If there are so few items, that the document does not scroll,
      // the scroll listener (that fetches more products) will not be triggered.
      // Therefore, check if the document does scroll at the moment and if not,
      // continue loading more products.
      const lastScrollTop = window.scrollY;
      const currentDocumentHeight = document.documentElement.clientHeight + lastScrollTop;
      const totalDocumentHeight = document.documentElement.scrollHeight;

      // when the menu is open, scroll is blocked on the body
      const scrollingAllowed = isScrollingAllowed();
      if (
        currentDocumentHeight === totalDocumentHeight &&
        scrollingAllowed &&
        eCatalog.currentView === ECatalogView.PRODUCTS
      ) {
        this.loadMoreProducts();
      }
    }
  }

  /**
   * Removes the empty state.
   */
  removeEmptyState(productGrid: HTMLElement): void {
    productGrid.classList.remove('e-catalog-products--empty');
    const emptyStateElem = productGrid.querySelector<HTMLElement>('.e-catalog-products__empty-state');
    if (emptyStateElem) {
      emptyStateElem.parentElement.removeChild(emptyStateElem);
    }
  }

  /**
   * show or hide the loading indicator.
   */
  showLoadingIndicator(show: boolean): void {
    const loadingIndicator = document.querySelector<HTMLElement>('.filter .indicators--loading');
    if (loadingIndicator) {
      loadingIndicator.style.visibility = show ? 'visible' : 'hidden';
    }
  }

  /**
   * When the user scrolls to the bottom of the page, automatically load more items
   */
  setupInfiniteScrollListener(): void {
    let lastScrollTop = window.scrollY;

    let isThrottling = false;
    const scrollListener = (): void => {
      if (eCatalog.currentView === ECatalogView.PRODUCTS) {
        const currentDocumentHeight = document.documentElement.clientHeight + lastScrollTop;
        const totalDocumentHeight = document.documentElement.scrollHeight;

        lastScrollTop = window.scrollY;
        if (!isThrottling) {
          setTimeout(() => {
            if (currentDocumentHeight >= totalDocumentHeight - this.loadingDistanceToBottom) {
              this.loadMoreProducts();
            }
            isThrottling = false;
          }, 16);
        }
        isThrottling = true;
      }
    };

    window.addEventListener('scroll', scrollListener, false);
  }

  /**
   * Call this function to load more products because the user scrolled to the end of the products list.
   */
  loadMoreProducts(): void {
    // now fetch the products
    if (!this.isLoadingProducts) {
      const moreProductsAvailable = this.currentNumberOfProducts < this.productResults.totalResults;
      if (moreProductsAvailable) {
        this.isLoadingProducts = true;
        const newNumberOfProducts = this.currentNumberOfProducts + this.productNumberStepSize;
        this.fetchProductData(newNumberOfProducts).then((products) => {
          this.productResults.results = [...this.productResults.results, ...products.results];
          this.renderProductTiles(this.currentNumberOfProducts);
          this.currentNumberOfProducts += this.productNumberStepSize;
          this.isLoadingProducts = false;
        });
      } else {
        // hide loading indicator
        this.showLoadingIndicator(false);
      }
    }
  }

  /**
   * Render the empty state inside the e-catalog tile grid, when no products are displayed.
   */
  renderProductsEmptyState(productGrid: HTMLElement, forSearch = true): void {
    this.removeEmptyState(productGrid);
    productGrid.classList.add('e-catalog-products--empty');
    const emptyStateElement = createHtmlElementFromString(this.productsEmptyState);
    const subTitle = emptyStateElement.querySelector<HTMLElement>('.e-catalog-products__empty-state-title span');
    const title = emptyStateElement.querySelector<HTMLElement>('.e-catalog-products__empty-state-title');
    const message = emptyStateElement.querySelector<HTMLElement>('.e-catalog-products__empty-state-message');
    const resetButton = emptyStateElement.querySelector<HTMLElement>('.button');
    const resetButtonLabel = resetButton.querySelector<HTMLElement>('span');

    // Insert translations
    if (forSearch) {
      const titleTextNode = document.createTextNode(this.translations.sorry + ' ');
      title.insertBefore(titleTextNode, subTitle);
      subTitle.innerText = this.translations.noResults;
      message.innerText = this.translations.searchForDifferentKeyword;
      resetButtonLabel.innerText = this.translations.resetSearch;
    } else {
      // empty state when no products available because of filters
      const titleTextNode = document.createTextNode(this.translations.sorryFilters + ' ');
      title.insertBefore(titleTextNode, subTitle);
      subTitle.innerText = this.translations.noResultsFiltered;
      message.innerText = this.translations.removeFilters;
      resetButtonLabel.innerText = this.translations.resetAllFilters;
    }

    // listen for clicks on the reset search button
    resetButton.addEventListener(
      'click',
      () => {
        if (this.currentSearchPhrase) {
          eCatalog.resetProductSearchInputField();
          eCatalog.removeSearchText();
        } else {
          this.resetFilters();
        }
      },
      false
    );

    // and finally append the empty state to the tile grid
    productGrid.appendChild(emptyStateElement);
  }

  /**
   * If necessary creates the discounts tooltip from the price information.
   */
  createDiscountsTooltip(product: HamiltonProductResult, discountButton: HTMLElement): void {
    if (product.price != undefined && product.price.length > 1) {
      // There are discounts available, add them to the tile
      const tooltipElement = createHtmlElementFromString(this.discountTooltipMarkup);

      // Insert tooltip headings
      tooltipElement.querySelector<HTMLElement>('.discount-tooltip__title').innerText = this.translations.discount;
      tooltipElement.querySelector<HTMLElement>('.discount-tooltip__subtitle').innerText = product.productName;

      // Insert headings for discount table
      tooltipElement.querySelector<HTMLElement>('tr th').innerText = this.translations.piece;
      tooltipElement.querySelector<HTMLElement>('tr th:last-child').innerText = this.translations.boxPrice;

      // For each discount option, add a row in the discounts table
      const discountsTable = tooltipElement.querySelector<HTMLTableElement>('table');
      product.price.sort((price1, price2) => {
        return price1.quantity - price2.quantity;
      });
      for (let i = 0; i < product.price.length; i++) {
        const discount = product.price[i];
        const discountRow = discountsTable.insertRow(i + 1);

        const piecesCell = discountRow.insertCell(0);
        piecesCell.innerText = discount.quantity.toString();

        const boxPriceCell = discountRow.insertCell(1);
        boxPriceCell.innerText = getPriceWithCurrency(discount);
      }

      // and finally append the tooltip to the discount button
      discountButton.appendChild(tooltipElement);
    } else {
      // hide the discounts button if there is only a single price
      discountButton.parentElement.removeChild(discountButton);
    }
  }

  /**
   * Retrieve the products.
   */
  fetchProductData(limit: number): Promise<HamiltonProductResults> {
    return new Promise((resolve) => {
      const offset = limit - this.productNumberStepSize;
      const searchPhraseExisted = this.currentSearchPhrase !== null && this.currentSearchPhrase !== undefined;
      getAllProducts(
        this.currentFilters,
        this.currentSearchPhrase,
        this.currentFirstLevelMenuItem?.id,
        this.currentSecondLevelMenuItem?.id,
        this.paths,
        this.currentCustomerId,
        this.currentVentilatorId,
        this.productNumberStepSize,
        offset
      ).then((results) => {
        if (searchPhraseExisted && results) {
          // If the user searched for something, update the number of results for each menu tab
          eCatalog.lastTaxonomyCounts = results.taxonomyCounts;
          eCatalog.lastTagCounts = results.tagCounts;
          eCatalog.updateSearchResultCountTags(results.taxonomyCounts);
        }
        resolve(results);
      });
    });
  }
}
